// `?board=<id>` — the board on screen, in the home's address
// (ASSISTANT_BOARD_MODE_SPEC.md §4 "`?board=<id>` in the URL";
// DESIGN-WL-ASSISTANT-MAIN-HOME-1). ws-home's half of the boards row.
//
// ONE value decides it: the boards row's current segment
// (functions/assistantBoard.js assistantBoardCurrentSegment) — the board the
// view shows, or 'home'. The address mirrors it; a cold load of a `?board=`
// link opens that board through the row's own switch (assistantBoardSelect),
// so a link and a click can never disagree.
//
// The write is a query-only history.replaceState — no navigation, no router
// dispatch, no new history entry (the same contract as the event detail
// sheet's `?event=` and moduleOpenBridge's setModuleRouteQuery), and never
// when the address already says it (no-op guard).
//
// A COLD LINK waits for Page mode to exist in this workspace
// (`_aiBoardsAvailable`, set by the view's probe) before it opens the board,
// and while it waits — and while the open is in flight — the mirror leaves
// the address alone, so the link is never stripped before it was honoured.
// Once the open settles the address follows the screen again: shown = kept;
// not shown (a failed read, the answers not in Page mode) = removed.
const PARAM = 'board'
const _boardUrlWin = () => (typeof window !== 'undefined' ? window : null)

export const homeBoardUrlRead = function homeBoardUrlRead() {
  const w = _boardUrlWin()
  if (!w || !w.location) return null
  try {
    const v = new URLSearchParams(w.location.search || '').get(PARAM)
    return v || null
  } catch (_) {
    return null
  }
}

// Mirror the current segment into the address. Returns true when it wrote.
export const homeBoardUrlSync = function homeBoardUrlSync(root) {
  const w = _boardUrlWin()
  if (!w || !w.history || !w.location || typeof w.history.replaceState !== 'function') return false
  const host = this
  const pending = host && host.__homeBoardUrl && host.__homeBoardUrl.pending
  if (pending) return false
  const seg = this.call('assistantBoardCurrentSegment', root || {})
  const want = seg && seg !== 'home' ? String(seg) : null
  let url
  try {
    url = new URL(w.location.href)
  } catch (_) {
    return false
  }
  const have = url.searchParams.get(PARAM) || null
  if (have === want) return false
  if (want) url.searchParams.set(PARAM, want)
  else url.searchParams.delete(PARAM)
  w.history.replaceState(w.history.state, '', url.pathname + url.search + url.hash)
  return true
}

// A cold `?board=<id>`: open it once Page mode exists here, through the row's
// own switch. Returns a promise that settles when the open does (or null when
// there is nothing to open).
export const homeBoardUrlOpen = function homeBoardUrlOpen(root) {
  const host = this
  const r = root || {}
  const state = (host.__homeBoardUrl = host.__homeBoardUrl || { done: false, pending: false })
  if (state.done) return null
  const id = homeBoardUrlRead()
  if (!id) {
    state.done = true
    return null
  }
  if (r._aiBoardsAvailable !== true) {
    // not yet — keep the link until the view's probe answers
    state.pending = true
    return null
  }
  state.done = true
  if (this.call('assistantBoardCurrentSegment', r) === id) {
    state.pending = false
    return null
  }
  state.pending = true
  const settle = () => {
    state.pending = false
    homeBoardUrlSync.call(host, host.state && host.state.root ? host.state.root : r)
  }
  let out
  try {
    out = this.call('assistantBoardSelect', id)
  } catch (_) {
    out = null
  }
  return Promise.resolve(out).then(settle, settle)
}

// The row's one hook (onRender + onUpdate): open a cold link, then mirror.
export const homeBoardUrlTick = function homeBoardUrlTick(root) {
  homeBoardUrlOpen.call(this, root)
  return homeBoardUrlSync.call(this, root)
}
